import { isOptionEnable } from "@/components/configurator/global-option/helper";
import { GroupParentAttributeOptionSet, ParentAttributeOptionSet } from "@/components/configurator/global-option/types";
import { selectedRowAttributeSetType } from "@/context/quote-management-context";
import { getGroupModelMap } from "@/utils/config";
import { BodyUnitBody, GlobalOptions, GlobalOptionsData } from "@/utils/form-data-manager";
import { getParentAttributeSetKey } from "@/components/configurator/qm/grid-layout/cell/body-cells/body-attribute-select.handlers";
import { applyFilters } from "@/extensions/hooks";

// Still used by configurator-core-helper.ts for the old flat-pool dependency flow
export function buildOptionDependencyMap(dependentDataWithKey: any) {
  const map = new Map<string, string[]>();

  dependentDataWithKey.forEach((dep: any) => {
    map.set(String(dep.option_id), dep.valid_options.map(String));
  });

  return map;
}

// Parses a matrix cell into individual option ID strings.
// Handles comma-separated strings ("1124,1125") and arrays (["1124","1125"]).
function parseMatrixCell(cell: any): string[] {
  if (!cell && cell !== 0) return [];
  if (Array.isArray(cell)) return cell.map(String);
  return String(cell).split(",").map((s) => s.trim()).filter(Boolean);
}

// Core matrix filtering: reads current selectedOption values from the parent,
// filters the matrix to surviving rows, then sets isDisabled on every option.
// Used on both dropdown open (initializeParentWithMatrix) and selection change (updateSelectionsWithMatrix).
function applyMatrixToParent(parent: any, optionMatrix: Record<string, string>[]): any {
  const currentSelections: Record<string, string> = {};
  parent.child.forEach((child: any) => {
    if (child.selectedOption?.value) {
      currentSelections[child.attributeCode] = String(child.selectedOption.value);
    }
  });

  const updatedChild = parent.child.map((child: any) => {
    if (!child.isProduct) return child;

    const survivingRows = optionMatrix.filter((row) =>
      Object.entries(currentSelections).every(([code, val]) =>
        parseMatrixCell(row[code]).includes(String(val))
      )
    );

    const validValues = new Set(
      survivingRows.flatMap((row) => parseMatrixCell(row[child.attributeCode]))
    );

    const currentlySelectedValue = String(child.selectedOption?.value ?? "");

    const updatedOptions = child.labelOptions.options.map((option: any) => {
      const isSelected = currentlySelectedValue && String(option.value) === currentlySelectedValue;
      // Empty validValues = no valid options for this attr (None product, impossible combo) → disable all
      const isDisabled = isSelected ? false : (validValues.size > 0 ? !validValues.has(String(option.value)) : true);
      return { ...option, isDisabled };
    });

    return { ...child, labelOptions: { ...child.labelOptions, options: updatedOptions } };
  });

  const parentSelectedOptions = updatedChild
    .map((child: any) => child.selectedOption?.value)
    .filter(Boolean)
    .map(String);

  return { ...parent, child: updatedChild, parentSelectedOptions };
}

// Original flat-pool dropdown-open injection — kept as fallback in case the matrix
// approach needs to be rolled back quickly. Not called by any active code path.
export function injectDependenciesOnDropdownOpen(
  bodyGroupParentAttributeOptionSet: any,
  groupId: any,
  selectedRowAttributeSetName: any,
  dependentDataWithKey: any,
): Map<string, ParentAttributeOptionSet> {
  const parents = bodyGroupParentAttributeOptionSet?.get(groupId)?.parents;

  if (!parents || !dependentDataWithKey) {
    return parents ?? new Map<string, ParentAttributeOptionSet>();
  }

  const dependencyMap = buildOptionDependencyMap(dependentDataWithKey);
  const groupNameLower = selectedRowAttributeSetName.toLowerCase();

  const updatedParents = new Map<string, ParentAttributeOptionSet>();

  parents.forEach((parent: any, parentKey: any) => {
    // default: unchanged parent
    let updatedParent = parent;

    if (
      typeof parentKey === "string" &&
      parentKey.toLowerCase().includes(groupNameLower)
    ) {
      updatedParent = {
        ...parent,
        child: parent.child.map((attribute: any) => ({
          ...attribute,
          labelOptions: {
            ...attribute.labelOptions,
            options: attribute.labelOptions.options.map((option: any) => {
              const validOptions = dependencyMap.get(String(option.value));

              const parentSelectedOptions = parent.parentSelectedOptions ?? [];
              const isEnabled = attribute.isProduct
                ? isOptionEnable(
                    parentSelectedOptions,
                    validOptions ?? option.enabledWithOptionIds,
                  )
                : true;

              return {
                ...option,
                isDisabled: !isEnabled,
                enabledWithOptionIds:
                  validOptions ?? option.enabledWithOptionIds,
              };
            }),
          },
        })),
      };
    }

    updatedParents.set(parentKey, updatedParent);
  });

  return updatedParents;
}

// Matrix-based replacement for injectDependenciesOnDropdownOpen.
// Called on dropdown open to set initial isDisabled state using matrix data only (no flat pool).
// Only processes the parent matching selectedRowAttributeSetName; others are left unchanged.
export function initializeParentWithMatrix(
  bodyGroupParentAttributeOptionSet: any,
  groupId: any,
  selectedRowAttributeSetName: any,
  parentMatrixMap: Map<string, Record<string, string>[]>,
): Map<string, ParentAttributeOptionSet> {
  const parents = bodyGroupParentAttributeOptionSet?.get(groupId)?.parents;
  if (!parents) return new Map<string, ParentAttributeOptionSet>();

  const groupNameLower = selectedRowAttributeSetName.toLowerCase();
  const updatedParents = new Map<string, ParentAttributeOptionSet>();

  parents.forEach((parent: any, parentKey: any) => {
    const isTargetParent =
      typeof parentKey === "string" &&
      parentKey.toLowerCase().includes(groupNameLower);

    const optionMatrix = parentMatrixMap.get(parentKey);

    if (!isTargetParent) {
      updatedParents.set(parentKey, parent);
      return;
    }

    // No matrix entry for this parent (e.g. Finish is in MATRIX_SKIP_GROUPS).
    // - Nothing selected → enable all options so the user can freely pick.
    // - Something already selected → pass [] to applyMatrixToParent so mutual
    //   exclusivity is enforced (other options stay disabled).
    if (!Array.isArray(optionMatrix)) {
      const hasSelection = parent.child.some((child: any) => child.selectedOption?.value);
      if (!hasSelection) {
        updatedParents.set(parentKey, {
          ...parent,
          child: parent.child.map((child: any) => ({
            ...child,
            labelOptions: {
              ...child.labelOptions,
              options: child.labelOptions.options.map((opt: any) => ({ ...opt, isDisabled: false })),
            },
          })),
        });
      } else {
        updatedParents.set(parentKey, applyMatrixToParent(parent, []));
      }
      return;
    }

    updatedParents.set(parentKey, applyMatrixToParent(parent, optionMatrix));
  });

  return updatedParents;
}

// Finish is excluded: its two attributes (Paint Grip / Stainless Steel) are
// mutually exclusive across attributes, not filtered by the product matrix.
// Same as the body cell's toggleDropdown, so Finish is never auto-selected.
const MATRIX_SKIP_GROUPS = ["Finish"];

// Builds parentKey → option_matrix for all parents of a group from a body's parsed
// option dependencies (parseDependencyData), e.g. "Cutting Board Default" → "Cutting Board" matrix.
function buildParentMatrixMap(
  optionSet: GroupParentAttributeOptionSet | undefined,
  groupId: string,
  dependencyData: Record<string, any>,
): Map<string, Record<string, string>[]> {
  const parentMatrixMap = new Map<string, Record<string, string>[]>();
  const group = optionSet?.get(groupId);
  if (!group || MATRIX_SKIP_GROUPS.includes(group.groupName)) return parentMatrixMap;

  group.parents.forEach((_, parentKey) => {
    const parentName = parentKey.replace(/\s*Default\s*$/i, "").trim();
    const matrix = dependencyData[parentName]?.option_matrix;
    if (Array.isArray(matrix)) {
      parentMatrixMap.set(parentKey, matrix);
    }
  });

  return parentMatrixMap;
}

// Global Options tick / Add body: once the global picks of a group are applied to a body, runs the
// "body.dropdownUpdated" plugins (e.g. auto-select-single-option) on that body's group as
// its cell dropdown would show it when opened, so an attribute the body's matrix leaves with
// one usable option is selected with the tick. Returns the body's picks with those selections.
export function applyDropdownPluginsOnGlobalApply(
  optionSet: GroupParentAttributeOptionSet,
  groupId: string,
  picks: GlobalOptions[],
  dependencyData: Record<string, any>,
): GlobalOptions[] {
  const group = optionSet.get(groupId);
  const parentMatrixMap = buildParentMatrixMap(optionSet, groupId, dependencyData);
  if (!group || parentMatrixMap.size === 0) return picks;

  // The group's selections are the body's picks, not the global row's
  const findPick = (attributeCode: string) =>
    picks.find(
      (pick) => String(pick.group_key) === String(groupId) && pick.key === attributeCode,
    );
  const parents = new Map<string, ParentAttributeOptionSet>();
  group.parents.forEach((parent, parentKey) => {
    parents.set(parentKey, {
      ...parent,
      child: parent.child.map((child) => {
        const pick = findPick(child.attributeCode);
        const option = child.labelOptions.options.find(
          (opt) => String(opt.value) === String(pick?.value),
        );
        return {
          ...child,
          selectedOption: pick?.value
            ? { label: option?.label ?? pick.label ?? "", value: String(pick.value), isDisabled: false }
            : { label: "", value: "", isDisabled: false },
        };
      }),
    });
  });

  const bodyOptionSet = new Map(optionSet).set(groupId, { ...group, parents });
  bodyOptionSet.set(groupId, {
    ...group,
    parents: initializeParentWithMatrix(bodyOptionSet, groupId, group.groupName, parentMatrixMap),
  });

  return applyFilters(
    "body.dropdownUpdated",
    { optionSet: bodyOptionSet, picks },
    { trigger: "globalApply", groupId, parentMatrixMap },
  ).picks;
}

const multipleModelMap = getGroupModelMap();

// Helper function to check if model exists for a specific group
const isNoneSku = (sku?: string) => sku?.toLowerCase().startsWith("none");

export const hasModelForGroup = (
  groupName: string,
  index: number,
  bodies: BodyUnitBody[],
): boolean => {
  const additionalData = bodies[index]?.global_picks_data?.additionalData;
  if (!additionalData || !groupName) return false;

  if(groupName === "Finish" && bodies[index]?.global_picks_data?.global_picks.some((pick) => pick.key === "finish_stainless_steel" && pick.value !== "")) {
    return true;
  }

  // Check base SKU
  const baseSku = additionalData
    .find((group) => group.key === groupName)
    ?.products?.map((product) => product.sku)
    .join(",");

  // Check additional group SKUs
  const additionalGroupNames: String[] = multipleModelMap.get(groupName) || [];

  const additionalSkus = additionalGroupNames.flatMap((groupName) =>
    additionalData
      .filter((group) => group.key === groupName)
      .flatMap((group) => group.products)
      .map((product) => product.sku)
      .filter((sku) => !isNoneSku(sku)),
  );

  if (!baseSku) return false;

  const combinedSku = additionalSkus.length
    ? `${baseSku}, ${additionalSkus.join(", ")}`
    : baseSku;

  return Boolean(combinedSku && combinedSku.trim());
};

export const hasSelectionForGroup = (
  groupId: string,
  bodyGroupParentAttributeOptionSet: any,
  index: number,
  bodies: BodyUnitBody[],
  parentAttributeSetKey: string,
): boolean => {
  const picks = bodies[index]?.global_picks_data?.global_picks;
  if (!Array.isArray(picks)) return false;
  const groupData = bodyGroupParentAttributeOptionSet?.get(groupId);

  // Find the parent that matches the parentAttributeSetKey
  let matchingParent = null;
  for (const parentEntry of groupData?.parents.values() ?? []) {
    if (parentEntry.parentAttributeSetKey === parentAttributeSetKey) {
      matchingParent = parentEntry;
      break;
    }
  }

  // Safely access child array and check if it has items
  const isProduct =
    Array.isArray(matchingParent?.child) && matchingParent.child.length > 0
      ? matchingParent.child[0].isProduct
      : undefined;

  if (!isProduct) return false;

  return picks.some(
    (pick) =>
      pick.group_key === groupId &&
      Boolean(pick.value && String(pick.value).trim()),
  );
};

export function shouldDisableTickButton(groupData: any): boolean {
  if (
    groupData?.groupName === "Finish" ||
    groupData?.groupName === "Kickplates"
  )
    return false;

  const parents = groupData?.parents;

  if (!parents || parents.size === 0) return true;

  let hasAnySelection = false;
  let hasMissingSelection = false;

  for (const parent of parents.values()) {
    for (const child of parent.child ?? []) {
      const options = child.labelOptions?.options ?? [];

      // Check if there is at least one enabled option
      const hasEnabledOption = options.some(
        (option: any) => !option.isDisabled,
      );

      // If user can't select anything, ignore this attribute
      if (!hasEnabledOption) continue;

      const selectedValue = child?.selectedOption?.value;

      const isSelected = selectedValue !== null && selectedValue !== "";

      // Has selectable options but nothing selected → DISABLE
      if (isSelected) {
        hasAnySelection = true;
      } else {
        hasMissingSelection = true;
      }
    }
  }

  // Nothing selected anywhere → ENABLE
  if (!hasAnySelection) return false;

  // Some selected but some missing → DISABLE
  if (hasMissingSelection) return true;

  // All selected → ENABLE
  return false;
}

export const updateAdditionalData = (
  additionalData: any[],
  selectedDropDownAttributeSet: selectedRowAttributeSetType,
  bodyGroupAttributeOptionSet: any,
) => {
  if (!Array.isArray(additionalData)) return additionalData;

  const selectedName = selectedDropDownAttributeSet.attributeSetName;
  const selectedId = String(selectedDropDownAttributeSet.attributeSetId);
  const selectedGroupId = selectedDropDownAttributeSet.groupId;

  // Find dependent parent IDs
  const groupData = bodyGroupAttributeOptionSet.get(selectedGroupId);
  let dependentParentIds: string[] = [];

  if (groupData?.parents) {
    for (const [, parent] of groupData.parents.entries()) {
      if (
        String(parent.parentAttributeSetKey) === selectedId &&
        Array.isArray(parent.dependentParent)
      ) {
        dependentParentIds = parent.dependentParent.map(String);
        break;
      }
    }
  }
  let dependentParentName: string | null = null;

  if (dependentParentIds.length && groupData?.parents) {
    const targetParentId = dependentParentIds[0];

    for (const [key, parent] of groupData.parents.entries()) {
      if (String(parent.parentAttributeSetKey) === targetParentId) {
        // key = "Kickplates Paint Grip Default"
        dependentParentName = key.replace(/\s*Default\s*$/i, "").trim();
        break;
      }
    }
  }

  // Build updated additionalData
  const updatedAdditionalData: any[] = [];

  for (const item of additionalData) {
    const product = item.products?.[0];
    if (!product) {
      updatedAdditionalData.push(item);
      continue;
    }

    let shouldDelete = false;

    // Delete same attribute set
    if (
      product.attribute_set_value === selectedName ||
      String(product.attribute_set_id) === selectedId
    ) {
      shouldDelete = true;
    }

    // Delete dependent products
    if (
      dependentParentIds.includes(String(product.attribute_set_id)) ||
      product.attribute_set_value === dependentParentName
    ) {
      shouldDelete = true;
    }

    if (!shouldDelete) {
      updatedAdditionalData.push(item); // keep
    }
  }

  return updatedAdditionalData;
};

/**
 * A body's global picks data with `groupIds` cleared completely, picks and products,
 * e.g. the groups the rules (src/rules) block after a tick.
 */
export const clearGroupsFromGlobalPicksData = (
  globalPicksData: GlobalOptionsData,
  groupIds: string[],
  bodyGroupAttributeOptionSet: GroupParentAttributeOptionSet,
): GlobalOptionsData => ({
  global_picks: globalPicksData.global_picks.map((pick) =>
    groupIds.includes(String(pick.group_key)) ? { ...pick, value: "", label: "" } : pick,
  ),
  additionalData: groupIds.reduce((additionalData, groupId) => {
    const group = bodyGroupAttributeOptionSet.get(groupId);
    if (!group) return additionalData;
    return updateAdditionalData(
      additionalData,
      {
        attributeSetId: getParentAttributeSetKey(group.parents, group.groupName),
        attributeSetName: group.groupName,
        groupId,
      },
      bodyGroupAttributeOptionSet,
    );
  }, globalPicksData.additionalData ?? []),
});

export function updateSelectionsWithMatrix(
  bodyGroupParentAttributeOptionSet: GroupParentAttributeOptionSet,
  selectedOption: any,
  attribute: any,
  parentKey: string,
  groupId: string,
  optionMatrix: Record<string, string>[] | null,
): GroupParentAttributeOptionSet {
  const updatedMap = new Map(bodyGroupParentAttributeOptionSet);
  const groupData = updatedMap.get(groupId);
  if (!groupData) return updatedMap;

  const updatedParents = new Map(groupData.parents);
  const parent = updatedParents.get(parentKey);
  if (!parent) return updatedMap;

  // Detect None/No for cross-parent dependency (must check before toggle)
  const nanValue = ["None", "No"];
  const isTogglingOff = attribute.selectedOption?.value === selectedOption?.value;
  const isNanSelected = !isTogglingOff && nanValue.includes(selectedOption?.label ?? "");
  const isNanJustUnselected = isTogglingOff && nanValue.includes(selectedOption?.label ?? "");

  // 1. Toggle the clicked attribute
  const toggledChild = parent.child.map((child: any) => {
    if (child.attributeCode !== attribute.attributeCode) return child;
    return {
      ...child,
      selectedOption: isTogglingOff ? { label: "", value: "", isDisabled: false } : selectedOption,
    };
  });

  // 2. Apply matrix filtering.
  //    - With a real matrix: use it to filter options.
  //    - No matrix + deselecting (isTogglingOff): re-enable all options so the user
  //      can freely pick a different option (e.g. Finish: deselect Paint Grip → enable all).
  //    - No matrix + selecting: use empty matrix so applyMatrixToParent disables all
  //      other options, preserving the mutual-exclusivity rule for groups like Finish.
  let updatedParentData: any;
  if (Array.isArray(optionMatrix)) {
    updatedParentData = applyMatrixToParent({ ...parent, child: toggledChild }, optionMatrix);
  } else if (isTogglingOff) {
    const enabledChild = toggledChild.map((child: any) => ({
      ...child,
      labelOptions: {
        ...child.labelOptions,
        options: child.labelOptions.options.map((opt: any) => ({ ...opt, isDisabled: false })),
      },
    }));
    updatedParentData = {
      ...parent,
      child: enabledChild,
      parentSelectedOptions: enabledChild
        .map((child: any) => child.selectedOption?.value)
        .filter(Boolean)
        .map(String),
    };
  } else {
    updatedParentData = applyMatrixToParent({ ...parent, child: toggledChild }, []);
  }
  updatedParents.set(parentKey, updatedParentData);

  // 3. Cross-parent None/No dependency
  if ((isNanSelected || isNanJustUnselected) && parent.dependentParent?.length) {
    parent.dependentParent.forEach((depParentKey: string) => {
      let depParentName = "";
      updatedParents.forEach((p: any, name: string) => {
        if (String(p.parentAttributeSetKey) === String(depParentKey)) {
          depParentName = name;
        }
      });
      if (!depParentName) return;

      const depParent = updatedParents.get(depParentName) as any;
      if (!depParent?.isDependentParent) return;

      if (isNanSelected) {
        updatedParents.set(depParentName, {
          ...depParent,
          parentSelectedOptions: [],
          child: depParent.child.map((child: any) => ({
            ...child,
            selectedOption: child.selectedOption?.value
              ? { label: "", value: "", isDisabled: false }
              : child.selectedOption,
            labelOptions: {
              ...child.labelOptions,
              options: child.labelOptions.options.map((option: any) => ({
                ...option,
                isDisabled: child.isProduct ? true : false,
              })),
            },
          })),
        });
      } else {
        updatedParents.set(depParentName, {
          ...depParent,
          child: depParent.child.map((child: any) => ({
            ...child,
            labelOptions: {
              ...child.labelOptions,
              options: child.labelOptions.options.map((option: any) => ({
                ...option,
                isDisabled: false,
              })),
            },
          })),
        });
      }
    });
  }

  updatedMap.set(groupId, { ...groupData, parents: updatedParents });
  return updatedMap;
}
